import React, { useMemo, useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ICONS } from "../../../utils/fontawesome";

const formatDateInputValue = (value) => {
  if (!value) return "";
  const dateObj = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return "";
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeTimeValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${String(Math.max(0, Math.min(23, value))).padStart(2, "0")}:00:00`;
  }
  if (typeof value === "string") {
    if (/^\d{2}:\d{2}:\d{2}$/.test(value)) return value;
    if (/^\d{2}:\d{2}$/.test(value)) return `${value}:00`;
    if (/^\d{1,2}$/.test(value)) {
      return `${String(value).padStart(2, "0")}:00:00`;
    }
  }
  return null;
};

const addHoursToTime = (timeStr, hoursToAdd = 2) => {
  if (!timeStr) return null;
  const base = new Date(`1970-01-01T${timeStr}`);
  if (Number.isNaN(base.getTime())) return null;
  base.setHours(base.getHours() + Number(hoursToAdd || 0));
  return base.toTimeString().slice(0, 8);
};

const formatTimeLabel = (timeStr) => {
  if (!timeStr) return "--";
  const [hour = "00", minute = "00"] = timeStr.split(":");
  return `${hour}h${minute}`;
};

const extractZoneLabel = (item) => {
  if (!item) return "Zone inconnue";
  const zoneField = item.zones;
  if (typeof zoneField === "string" && zoneField.trim()) {
    return zoneField;
  }
  return "Zone inconnue";
};

const getCreneauId = (item, index) => {
  const zoneLabel = extractZoneLabel(item);
  const zoneIdentifier =
    item?.zone_id ??
    (item?.zone &&
      typeof item.zone === "object" &&
      (item.zone.id || item.zone.zone_id)) ??
    zoneLabel;
  const timeValue = item?.creneau_heure_debut ?? null;
  const dateValue = item?.date ?? item?.jour ?? null;
  const fallback =
    zoneIdentifier || timeValue || dateValue
      ? [zoneIdentifier, dateValue, timeValue].filter(Boolean).join("|")
      : null;
  return (
    item?.creneau_id ??
    (fallback && index != null ? `${fallback}|${index}` : fallback) ??
    (index != null ? `slot-${index}` : null)
  );
};

const extractAtelierLabel = (atelier) => {
  if (!atelier) return "Atelier inconnu";
  if (Array.isArray(atelier)) {
    return atelier.length ? atelier.join(", ") : "Atelier inconnu";
  }
  if (typeof atelier === "object") {
    return atelier.nom || atelier.name || "Atelier inconnu";
  }
  return String(atelier);
};

const PlanningAgenda = ({
  selectedDate,
  onDateChange,
  creneaux = [],
  onSelectCreneau,
}) => {
  const [selectedCreneauId, setSelectedCreneauId] = useState(null);
  const [sortByZone, setSortByZone] = useState(false);

  const handleDateInputChange = useCallback(
    (event) => {
      if (!onDateChange) return;
      const value = event.target.value;
      if (!value) {
        onDateChange(null);
        return;
      }
      const parsedDate = new Date(`${value}T00:00:00`);
      if (!Number.isNaN(parsedDate.getTime())) {
        onDateChange(parsedDate);
      }
    },
    [onDateChange]
  );

  const agendaSlots = useMemo(() => {
    if (!Array.isArray(creneaux)) return [];
    const filtered =
      creneaux
        .filter(
          (item) =>
            item &&
            (item.creneau_heure_debut ||
              item.heure_debut ||
              item.heure ||
              item.status)
        ) || [];
    return filtered.map((item, index) => {
      const heureDebutRaw =
        item?.creneau_heure_debut ?? item?.heure_debut ?? item?.heure ?? null;
      const heureFinRaw =
        item?.creneau_heure_fin ?? item?.heure_fin ?? item?.fin ?? null;

      const normalizedStart = normalizeTimeValue(heureDebutRaw);
      let normalizedEnd = normalizeTimeValue(heureFinRaw);
      if (!normalizedEnd && normalizedStart) {
        normalizedEnd = addHoursToTime(normalizedStart, item?.duree || 2);
      }

      const zoneLabel = extractZoneLabel(item);
      return {
        id: getCreneauId(item, index),
        atelierLabel: extractAtelierLabel(item?.atelier),
        status: item?.status || "",
        startLabel: formatTimeLabel(normalizedStart),
        startValue: normalizedStart,
        zoneLabel,
        endLabel: normalizedEnd ? formatTimeLabel(normalizedEnd) : null,
        placesDispo: Number(item?.places ?? 0),
        placesPrises: Number(item?.place_reservee ?? 0),
        tablesDispo: Number(item?.tables ?? 0),
        tablesPrises: Number(item?.table_reservee ?? 0),
        creneauReserves: Number(item?.creneau_total ?? 0),
        rawItem: item,
      };
    });
  }, [creneaux]);

  const summary = useMemo(() => {
    const totalPlacesPrises = agendaSlots.reduce(
      (acc, slot) => acc + (Number(slot.placesPrises) || 0),
      0
    );
    const totalCreneauxReserves = agendaSlots.reduce(
      (acc, slot) => acc + (Number(slot.creneauReserves) || 0),
      0
    );
    return { totalPlacesPrises, totalCreneauxReserves };
  }, [agendaSlots]);

  useEffect(() => {
    if (!selectedCreneauId) return;
    const stillExists = agendaSlots.some(
      (slot) => slot?.id === selectedCreneauId
    );
    if (!stillExists) {
      setSelectedCreneauId(null);
      if (onSelectCreneau) {
        onSelectCreneau(null);
      }
    }
  }, [agendaSlots, selectedCreneauId, onSelectCreneau]);

  const selectedDateLabel = selectedDate
    ? new Date(selectedDate).toLocaleDateString("fr-FR", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "Aucune date sélectionnée";

  return (
    <div className="flex flex-col gap-4 text-slate-800">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h2 className="text-[#F36D26] text-3xl font-bold">Planning</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSortByZone((prev) => !prev)}
              className="flex items-center gap-2 text-sm text-slate-600 border border-slate-200 px-3 py-1.5 rounded-lg hover:border-slate-400 hover:text-slate-900 transition-colors bg-white"
            >
              <FontAwesomeIcon
                icon={sortByZone ? ICONS.toggleOn : ICONS.toggleOff}
                className="h-4 w-4 text-orange-600"
              />
              <span>Tri par {sortByZone ? "zone" : "heure"}</span>
            </button>
            <input
              type="date"
              value={formatDateInputValue(selectedDate)}
              onChange={handleDateInputChange}
              className="px-3 py-2 rounded-md border border-slate-300 bg-white text-slate-800 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#F36D26]"
            />
          </div>
        </div>
        <p className="text-slate-500 text-sm">{selectedDateLabel}</p>
      </div>

      <div className="bg-white rounded-xl p-3 shadow text-sm text-slate-700 flex items-center justify-between px-10">
        <div>
          <p className="font-semibold">Places prises</p>
          <p className="text-lg text-orange-600 text-center">{summary.totalPlacesPrises}</p>
        </div>
        <div className="text-right">
          <p className="font-semibold">Créneaux réservés</p>
          <p className="text-lg text-orange-600 text-center">{summary.totalCreneauxReserves}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 max-h-[65vh] overflow-y-auto space-y-3 shadow">
        {agendaSlots.length === 0 ? (
          <p className="text-slate-500 text-center italic">
            Aucun créneau disponible pour cette date.
          </p>
        ) : (
          [...agendaSlots]
            .sort((a, b) => {
              if (sortByZone) {
                const zoneA = (a.zoneLabel || "").toLowerCase();
                const zoneB = (b.zoneLabel || "").toLowerCase();
                if (zoneA === zoneB) {
                  return (a.startValue || "").localeCompare(b.startValue || "");
                }
                return zoneA.localeCompare(zoneB);
              }
              return (a.startValue || "").localeCompare(b.startValue || "");
            })
            .map((slot) => {
            const isSelected = slot.id && slot.id === selectedCreneauId;
            const hideCapacities =
              slot.status &&
              typeof slot.status === "string" &&
              slot.status.toLowerCase().includes("cloture exceptionnel");
            return (
              <button
                key={slot.id}
                type="button"
                onClick={() => {
                  if (!slot.id) return;
                  if (slot.id === selectedCreneauId) {
                    setSelectedCreneauId(null);
                    if (onSelectCreneau) {
                      onSelectCreneau(null);
                    }
                    return;
                  }
                  setSelectedCreneauId(slot.id);
                  if (onSelectCreneau) {
                    onSelectCreneau({
                      id: slot.id,
                      heure: slot.startValue,
                      zone:
                        slot.rawItem?.zone_id ??
                        slot.rawItem?.zoneId ??
                        slot.rawItem?.zone ??
                        slot.zoneLabel ??
                        null,
                      data: slot.rawItem,
                    });
                  }
                }}
                className={`w-full text-left border rounded-xl px-4 py-3 transition-colors ${
                  isSelected
                    ? "bg-pink-200 border-orange-300"
                    : "bg-white border-slate-200 hover:border-slate-400"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-lg font-semibold flex flex-wrap items-center gap-2">
                    <span className="text-sm text-slate-500 uppercase tracking-wide">
                      {slot.zoneLabel}
                    </span>
                    <span>
                      {slot.startLabel}
                      {slot.endLabel ? ` - ${slot.endLabel}` : ""}
                    </span>
                  </p>
                  {slot.status && (
                    <span
                      className={`text-xs uppercase tracking-wide px-2 py-1 rounded ${
                        slot.status.includes("disponibilite")
                          ? "bg-green-200 text-green-900"
                          : "bg-orange-200 text-orange-900"
                      }`}
                    >
                      {slot.status}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-600">
                  {slot.atelierLabel}
                </p>
                {!hideCapacities && (
                  <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-2 text-sm text-slate-600">
                    <p>
                      Places :{" "}
                      <span className="font-semibold">
                        {slot.placesPrises} prises / {slot.placesDispo} dispo
                      </span>
                    </p>
                    <p>
                      Tables :{" "}
                      <span className="font-semibold">
                        {slot.tablesPrises} prises / {slot.tablesDispo} dispo
                      </span>
                    </p>
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PlanningAgenda;
